import React, { useEffect, useState, useRef } from "react";
import { Container } from "./style";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, Input, Modal, message, Progress, Select } from "antd";
import {
  LeftOutlined,
  FileTextOutlined,
  BulbOutlined,
  AimOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import ReactRouterPrompt from "react-router-prompt";
import MockPhone from "../../../components/MockPhone/index";
import ScheduleMenu from "../../../components/ScheduleMenu/index";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { setActionData } from "../../../store/features/CreateAction";
import useSubscriptionCheck from "../../../hooks/useSubscriptionCheck";
import SubscriptionModal from "../../../components/SubscriptionModal";

const { TextArea } = Input;

const CREATE_ACTION = gql`
  mutation createAction($createActionInput: CreateActionInput!) {
    createAction(payload: $createActionInput) {
      _id
    }
  }
`;
const UPDATE_ACTION = gql`
  mutation updateAction($updateActionInput: UpdateActionInput!) {
    updateAction(payload: $updateActionInput) {
      _id
    }
  }
`;

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
    }
  }
`;


export default function CreateAction() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [groups, setGroups] = useState([]);
  const [createAction] = useMutation(CREATE_ACTION);
  const [updateAction] = useMutation(UPDATE_ACTION);
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [params] = useSearchParams();
  const location = useLocation();
  const rowData = useSelector((state) => state.action.actionData);
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const actionId = rowData?._id;

  // Subscription check
  const {
    showModal,
    checkAndExecute,
    handleSubscriptionSuccess,
    handleModalCancel,
    upgradePromptKind,
  } = useSubscriptionCheck();

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo.coach?._id, isArchived: false },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  useEffect(() => {
    if (location.pathname === "/CreateAction" && rowDataRef.current._id) {
      dispatch(setActionData({}));
    }
    if (location.pathname === "/EditAction" && !rowDataRef.current._id) {
      navigate("/CreateAction");
    }
  }, [location, dispatch, navigate]);
  // Form
  const [form] = Form.useForm();

  // Update action
  useEffect(() => {
    if (form && rowData._id) {
      let visibilityValue = "ALL_SUBSCRIBERS"; // 默认值
      if (rowData.visibilityType === "ALL_SUBSCRIBERS") {
        visibilityValue = "ALL_SUBSCRIBERS";
      } else if (
        rowData.visibilityType === "GROUP" &&
        rowData.visibilityGroupId
      ) {
        visibilityValue = `GROUP_${rowData.visibilityGroupId}`;
      }
      form.setFieldsValue({
        ...rowData,
        visibility: visibilityValue,
      });
    }
  }, [rowData, form]);

  // Get content from AI tool and fill to the form
  const actionContent =
    params && params.getAll("content") && params.getAll("content")[0];
  useEffect(() => {
    if (actionContent) {
      form.setFieldsValue({ content: actionContent });
    }
  }, [actionContent, form]);

  const contentValue = Form.useWatch("content", form);
  const visibilityValue = Form.useWatch("visibility", form);

  useEffect(() => {
    if (
      (contentValue && contentValue !== rowDataRef.current.content) ||
      (visibilityValue && visibilityValue !== rowDataRef.current.visibilityValue)
    ) {
      setIsChanged(true);
    }
  }, [contentValue, visibilityValue]);
  useEffect(() => {
    if (loading) {
      setIsChanged(false);
    }
  }, [loading]);

  const onSubmit = async ({ values, status }) => {
    setLoading(true);
    try {
      if (!actionId) {
        const param = {
          content: values.content,
          isAIGenerated: false,
          isFromChallenge: false,
          status,
          createdBy: userInfo.coach?._id,
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue === "ALL_SUBSCRIBERS") {
          param.visibilityType = "ALL_SUBSCRIBERS";
        } else if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        await createAction({
          variables: {
            createActionInput: param,
          },
        });
        message.success("Action has been created!");
      } else {
        const param = {
          _id: actionId,
          content: values.content,
          status,
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue === "ALL_SUBSCRIBERS") {
          param.visibilityType = "ALL_SUBSCRIBERS";
        } else if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        await updateAction({
          variables: {
            updateActionInput: param,
          },
        });
        message.success("Action has been updated!");
      }
      dispatch(setActionData({}));
      navigate("/ContentManager?tabName=5");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    console.log("Success:", values);
    // Check subscription before publishing
    checkAndExecute(() => {
      onSubmit({ values, status: "PUBLISHED" });
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setLoading(false);
  };

  const onDraft = () => {
    const values = form.getFieldsValue();
    onSubmit({ values, status: "DRAFTED" });
  };

  const onBack = () => {
    navigate("/contentManager?tabName=5");
  };

  return (
    <Container forceRender>
      <ReactRouterPrompt when={isChanged}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              title="Unsaved changes"
              open
              footer={[
                <div key="flex-between" className="flex-between">
                  <Button
                    key="discard"
                    danger
                    loading={loading}
                    onClick={() => {
                      setIsChanged(false);
                      onConfirm();
                    }}
                  >
                    Discard Changes
                  </Button>
                  <div key="div">
                    <Button key="back" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      key="save"
                      type="primary"
                      loading={loading}
                      onClick={onDraft}
                    >
                      Save Draft
                    </Button>
                  </div>
                </div>,
              ]}
            >
              <p>
                You are about to leave this page with unsaved changes. Would you
                like to save these changes before leaving?
              </p>
            </Modal>
          )
        }
      </ReactRouterPrompt>
      <div className="page-form">
        <div className="left-side">
          <Button
            className="btn-back"
            icon={<LeftOutlined />}
            type="text"
            onClick={onBack}
          >
            Content Manager
          </Button>

          <div className="page-title">
            {!actionId ? "Create a new Action" : "Update an Action"}
          </div>
          <Form
            form={form}
            name="actionForm"
            layout="vertical"
            size="large"
            className="full-width"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label="Enter your action"
              name="content"
              rules={[{ required: true, message: "Please input action" }]}
            >
              <TextArea
                showCount
                maxLength={300}
                placeholder="Empty"
                style={{ resize: "none", height: 200 }}
              />
            </Form.Item>
            <Form.Item
              label="Visible to"
              name="visibility"
              rules={[{ required: true, message: "Please select visibility" }]}
            >
              <Select placeholder="Select visibility" size="large">
                <Select.Option value="ALL_SUBSCRIBERS">
                  All Subscribers
                </Select.Option>
                {groups.map((group) => (
                  <Select.Option key={group._id} value={`GROUP_${group._id}`}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <br />
            <Form.Item>
              <ScheduleMenu
                id={actionId}
                showSchedule={false}
                form={form}
                loading={loading}
              ></ScheduleMenu>
              <Button
                block
                className="mt-16"
                loading={loading}
                onClick={onDraft}
              >
                Save as draft
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <MockPhone showTabbar>
            <h3 className="challenge-top">Hello {userInfo?.coach?.name}</h3>
            <div className="mb-10">
              <div className="flex-between">
                <div>Today's progress</div>
                <div>0/1</div>
              </div>
              <Progress percent={0} showInfo={false} />
            </div>
            <div className="content-box">
              <div className="box-title flex-start">
                <div className="box-icon blue-icon">
                  <FileTextOutlined />
                </div>
                Daily Reflection
              </div>
              <div className="box-content">
                Take a moment to reflect on your emotional state today. What are
                three things you're grateful for, and how did they make you
                feel?
              </div>
              <Button type="primary" block>
                Write
              </Button>
            </div>
            <div className="content-box">
              <div className="box-title flex-start">
                <div className="box-icon yellow-icon">
                  <BulbOutlined />
                </div>
                Today's Insight
              </div>
              <div className="box-content">
                <div className="mb-10">
                  "The mind is everything. What you think you become." - Buddha.
                </div>
                <span className="c-primary">
                  Learn more
                  <ExportOutlined style={{ marginLeft: 5 }} />
                </span>
              </div>
              <Button type="primary" block>
                Mark Complete
              </Button>
            </div>
            <div className="content-box">
              <div className="box-title flex-start">
                <div className="box-icon pink-icon">
                  <AimOutlined />
                </div>
                Today's Action
              </div>
              <div className="box-content">{contentValue || "Empty"}</div>
              <Button type="primary" block>
                Mark Complete
              </Button>
            </div>
          </MockPhone>
        </div>
      </div>
      <SubscriptionModal
        open={showModal}
        onSuccess={handleSubscriptionSuccess}
        onCancel={handleModalCancel}
        checkoutPromptKind={upgradePromptKind ?? "trial"}
      />
    </Container>
  );
}
